import type { AaveKinkParams, DeltaStep, LinearApproxParams, LiquidityInfo, RateModelParams } from '../types'

/**
 * Hard ceiling for any rate returned by the models.
 * Anything beyond 10,000% APY is clearly an extrapolation artefact.
 */
const MAX_RATE = 10_000 // 0-100 scale → 10 000 %

function clampRate(rate: number): number {
  if (!Number.isFinite(rate)) return 0
  return Math.min(Math.max(rate, 0), MAX_RATE)
}

/**
 * Compute the borrow APY at a given utilization using the Aave v3 linear kink model.
 * All inputs are in 0-1 scale. Returns APY in 0-100 scale.
 *
 * The Aave model:
 *   if U <= U_optimal: borrowRate = base + (U / U_optimal) * slope1
 *   if U > U_optimal:  borrowRate = base + slope1 + ((U - U_optimal) / (1 - U_optimal)) * slope2
 *
 * Then supplyRate = borrowRate * U * (1 - reserveFactor)
 */
function aaveBorrowRate(utilization: number, params: AaveKinkParams): number {
  const { baseRate, slope1, slope2, optimalUtilization } = params
  const u = Math.min(Math.max(utilization, 0), 1)

  if (u <= optimalUtilization) {
    const rate = baseRate + (optimalUtilization > 0 ? (u / optimalUtilization) * slope1 : 0)
    return clampRate(rate * 100)
  }

  const excessUtil = u - optimalUtilization
  const excessCapacity = 1 - optimalUtilization
  const rate = baseRate + slope1 + (excessCapacity > 0 ? (excessUtil / excessCapacity) * slope2 : 0)
  return clampRate(rate * 100)
}

function aaveSupplyRate(utilization: number, params: AaveKinkParams): number {
  const borrowRate = aaveBorrowRate(utilization, params) / 100 // back to 0-1 for math
  const u = Math.min(Math.max(utilization, 0), 1)
  return clampRate(borrowRate * u * (1 - params.reserveFactor) * 100)
}

/**
 * Linear approximation: assume rate scales linearly with utilization
 * from the current observation point.
 *
 * We cap the extrapolation ratio at 5× to avoid blow-ups when the
 * current utilization is near zero but the new utilization is large.
 */
const MAX_LINEAR_RATIO = 5

function linearBorrowRate(utilization: number, params: LinearApproxParams): number {
  if (params.currentUtilization <= 1e-6) return params.currentRate
  const u = Math.min(Math.max(utilization, 0), 1)
  const ratio = Math.min(u / params.currentUtilization, MAX_LINEAR_RATIO)
  return clampRate(params.currentRate * ratio)
}

function linearSupplyRate(utilization: number, params: LinearApproxParams): number {
  // supplyRate ≈ borrowRate × u. With borrowRate ∝ u the supply rate ∝ u².
  // However this quadratic extrapolation explodes when currentUtilization is
  // tiny, so we cap the ratio just like the borrow side.
  if (params.currentUtilization <= 1e-6) return params.currentRate
  const u = Math.min(Math.max(utilization, 0), 1)
  const ratio = Math.min(u / params.currentUtilization, MAX_LINEAR_RATIO)
  return clampRate(params.currentRate * ratio * ratio)
}

/**
 * Compute borrow rate at a new utilization given the rate model.
 * Returns APY in 0-100 scale, clamped to [0, MAX_RATE].
 */
export function computeBorrowRate(utilization: number, model: RateModelParams): number {
  if (model.type === 'aave-kink') {
    return aaveBorrowRate(utilization, model)
  }
  return linearBorrowRate(utilization, model)
}

/**
 * Compute supply rate at a new utilization given the rate model.
 * Returns APY in 0-100 scale, clamped to [0, MAX_RATE].
 */
export function computeSupplyRate(utilization: number, model: RateModelParams): number {
  if (model.type === 'aave-kink') {
    return aaveSupplyRate(utilization, model)
  }
  return linearSupplyRate(utilization, model)
}

/**
 * Calculate the new borrow rate after borrowing `amountUsd` more from a market.
 * Borrowing increases utilization: newU = (totalBorrow + amount) / totalSupply
 */
export function borrowRateAfterTrade(liq: LiquidityInfo, amountUsd: number): number {
  if (liq.totalSupplyUsd <= 0) return 0
  const newBorrow = liq.totalBorrowUsd + amountUsd
  const newUtilization = Math.min(newBorrow / liq.totalSupplyUsd, 1)
  return computeBorrowRate(newUtilization, liq.rateModel)
}

/**
 * Calculate the new supply rate after depositing `amountUsd` more into a market.
 * Depositing increases total supply and decreases utilization:
 *   newU = totalBorrow / (totalSupply + amount)
 */
export function supplyRateAfterTrade(liq: LiquidityInfo, amountUsd: number): number {
  const newSupply = liq.totalSupplyUsd + amountUsd
  if (newSupply <= 0) return 0
  const newUtilization = liq.totalBorrowUsd / newSupply
  return computeSupplyRate(newUtilization, liq.rateModel)
}

/**
 * Find the maximum trade size (in USD) where the spread remains > 0.
 *
 * When you execute an arb:
 *   - Borrow `amount` from borrow market → utilization goes UP → borrow rate goes UP
 *   - Deposit `amount` into deposit market → utilization goes DOWN → supply rate goes DOWN
 *   - Spread = supplyRate(after) - borrowRate(after)
 *
 * We binary search for the amount where spread = 0.
 * Returns USD amount, capped by available liquidity on the borrow side.
 */
export interface MaxTradeResult {
  maxTradeUsd: number
  borrowAPYAtMax: number
  depositAPYAtMax: number
  spreadAtMax: number
  deltaSteps: DeltaStep[]
}

export function computeMaxTradeSize(borrowLiq: LiquidityInfo, depositLiq: LiquidityInfo): MaxTradeResult | undefined {
  // Cap at available borrow liquidity
  const maxBorrow = Math.max(0, borrowLiq.availableUsd)
  if (maxBorrow <= 0) return undefined

  // Check if spread is already <= 0 at zero trade (shouldn't happen, but safety)
  const spreadAtZero = computeSpreadAtAmount(borrowLiq, depositLiq, 0)
  if (spreadAtZero <= 0) return undefined

  // Check spread at max borrow
  const spreadAtMax = computeSpreadAtAmount(borrowLiq, depositLiq, maxBorrow)

  // If spread is still positive at max borrow, the whole available liquidity is tradeable
  if (spreadAtMax > 0) {
    const bAtMax = borrowRateAfterTrade(borrowLiq, maxBorrow)
    const dAtMax = supplyRateAfterTrade(depositLiq, maxBorrow)
    return {
      maxTradeUsd: maxBorrow,
      borrowAPYAtMax: bAtMax,
      depositAPYAtMax: dAtMax,
      spreadAtMax: dAtMax - bAtMax,
      deltaSteps: buildDeltaSteps(borrowLiq, depositLiq, maxBorrow),
    }
  }

  // Binary search for the crossover point
  let lo = 0
  let hi = maxBorrow
  const tolerance = Math.max(1, maxBorrow * 0.001) // $1 or 0.1% of max, whichever is larger

  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2
    const spreadAtMid = computeSpreadAtAmount(borrowLiq, depositLiq, mid)

    if (spreadAtMid > 0) {
      lo = mid
    } else {
      hi = mid
    }

    if (hi - lo < tolerance) break
  }

  const result = lo
  const bAtMax = borrowRateAfterTrade(borrowLiq, result)
  const dAtMax = supplyRateAfterTrade(depositLiq, result)
  return {
    maxTradeUsd: result,
    borrowAPYAtMax: bAtMax,
    depositAPYAtMax: dAtMax,
    spreadAtMax: dAtMax - bAtMax,
    deltaSteps: buildDeltaSteps(borrowLiq, depositLiq, result),
  }
}

const DELTA_PCTS = [0.25, 0.5, 0.75, 1.0]

export function buildDeltaSteps(borrowLiq: LiquidityInfo, depositLiq: LiquidityInfo, maxTradeUsd: number): DeltaStep[] {
  // Compute the model's own rates at zero trade as the baseline for deltas.
  // This avoids artifacts when the model rate doesn't exactly match the
  // displayed rate (e.g. APY vs APR, compounding, incentives).
  const baseBorrow = borrowRateAfterTrade(borrowLiq, 0)
  const baseDeposit = supplyRateAfterTrade(depositLiq, 0)

  return DELTA_PCTS.map((pct) => {
    const tradeUsd = maxTradeUsd * pct
    const borrowAPY = borrowRateAfterTrade(borrowLiq, tradeUsd)
    const depositAPY = supplyRateAfterTrade(depositLiq, tradeUsd)
    return {
      pct,
      tradeUsd,
      borrowAPY,
      depositAPY,
      spread: depositAPY - borrowAPY,
      borrowDelta: borrowAPY - baseBorrow,
      depositDelta: depositAPY - baseDeposit,
    }
  })
}

function computeSpreadAtAmount(borrowLiq: LiquidityInfo, depositLiq: LiquidityInfo, amountUsd: number): number {
  const newBorrowRate = borrowRateAfterTrade(borrowLiq, amountUsd)
  const newSupplyRate = supplyRateAfterTrade(depositLiq, amountUsd)
  return newSupplyRate - newBorrowRate
}
